<template>
    <Head :title="'Explore St. Johns & Nearby Communities'" >
        <link rel="canonical" href="https://www.ginabetbeze.com/neighborhoods" />
        <meta name="description" content="Explore St. Johns County and nearby communities with Gina Betbeze. Get insights on Julington Creek and other areas to find your perfect neighborhood." />
    </Head>

    <PageContent
        :id="'neighborhoods-page'"
        :breadcrumbs="['Home','Resources','Neighborhoods']"
        :header="'Neighborhoods'"
    >
        <BasicContainer>
            <div class="hidden" id="neighborhoods-section"></div>
            <SecondaryHeader>Popular Neighborhoods in Northeast Florida</SecondaryHeader>
            <Paragraph>
                Discover the charm and amenities of these popular neighborhoods in the Jacksonville area. Whether you're looking for modern open-concept living, waterfront views, walkable urban spaces, or something uniquely your own, I’m here to help you find the perfect match.
            </Paragraph>

            <div v-for="neighborhood in neighborhoods" :key="neighborhood.id">
                <SecondaryHeader :id="neighborhood.id">{{ neighborhood.name }}</SecondaryHeader>
                <Paragraph>
                    {{ neighborhood.description }}
                    <br />
                    <br />
                    <Link
                        :href="'/contact' + '#contact-breadcrumb'"
                        :data="{ subject: `I'm interested in a property in ${neighborhood.name}` }"
                        class="py-3 mr-4 nav-link rising-underline"
                        preserve-state
                    >
                        Got your eye on a property around here? I'd love to help you make it yours—reach out today!
                    </Link>
                </Paragraph>
                <div class="hidden tablet-sm:block w-full">
                    <iframe
                        :src="neighborhood.map"
                        width="100%"
                        height="450"
                        style="border: 0"
                        allowfullscreen=""
                        loading="lazy"
                        referrerpolicy="no-referrer-when-downgrade"
                    ></iframe>
                </div>
            </div>

            <SecondaryHeader>Your Guide to Finding the Perfect Home</SecondaryHeader>
            <Paragraph>
                Each of these neighborhoods offers unique benefits, but if you are curious about other areas on
                Northeast Florida or hidden gems, I am here to guide you. Every neighborhood has its own charm, and I'd
                love to help you discover the one that feels just right.
            </Paragraph>
            <Paragraph>
                <Link
                    :href="'/contact'"
                    :data="{ subject: `I'm interested in a property in...` }"
                    class="py-3 mr-4 nav-link rising-underline"
                    preserve-state
                >
                    Contact me today to learn more about our area and schedule a tour of available
                    properties.
                </Link>
            </Paragraph>
        </BasicContainer>
    </PageContent>
</template>
<script setup>
import HomeLayout from "../Layouts/HomeLayout.vue";
import PageContent from "../Components/Layout/PageContent.vue";
import BasicContainer from "../Components/PageContent/Containers/BasicContainer.vue";
import { Head, Link, usePage } from "@inertiajs/vue3";
import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import Breadcrumbs from "../Components/PageContent/Breadcrumbs.vue";
import MainHead from "@/Components/PageContent/MainHead.vue";
import Paragraph from "@/Components/PageContent/Paragraph.vue";
import SecondaryHeader from "@/Components/PageContent/SecondaryHeader.vue";

defineOptions({ layout: HomeLayout });
const page = usePage();
const props = defineProps({
    title: {
        type: String,
        required: true,
    },
});

const neighborhoods = [
    {
        id: "beaches",
        name: "Jacksonville Beach Real Estate",
        description:
            `The Beaches area offers a relaxed coastal lifestyle with beautiful oceanfront properties and a tight-knit community. Residents enjoy easy access to the beach, outdoor activities, and a variety of dining and entertainment options.`,
        map: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d130720.095436759!2d-81.50439527050779!3d30.32881521474092!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e4496c4e829197%3A0xae39392f65dda74!2sThe%20Beaches%2C%20Jacksonville%20Beach%2C%20FL!5e1!3m2!1sen!2sus!4v1724380789765!5m2!1sen!2sus",
    },
    {
        id: "jacksonville",
        name: "Southeast Jacksonville Homes",
        description:
            "These areas are home to some of Northeast Florida's most charming neighborhoods. San Marco stands out with its Mediterranean Revival architecture, boutique shops, and lively arts scene, centered around its iconic square with a fountain inspired by Venice’s Piazza San Marco. Strolling through San Marco, you'll find unique dining spots and a welcoming, close-knit community vibe. Southside and Baymeadows offer a mix of residential and commercial spaces, making it a convenient area for both work and leisure. Known for its shopping centers, green spaces, and diverse restaurants, Southside/Baymeadows is perfect for those who enjoy a blend of urban amenities with suburban comfort. Each neighborhood has its own unique charm, but all share Jacksonville's warmth and vibrancy, making them ideal for both locals and visitors alike.\n",
        map: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d220677.3039393498!2d-81.71282703499294!3d30.20618203567265!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e5cb1d2514a25d%3A0xdf133fe429ac679c!2sSouthside%2C%20Jacksonville%2C%20FL!5e0!3m2!1sen!2sus!4v1730576745499!5m2!1sen!2sus",
    },
    {
        id: "mandarin",
        name: "Mandarin real Estate",
        description:
            `Mandarin is a picturesque neighborhood in Jacksonville known for its historic charm, old oak trees, and riverfront properties. This area offers a tranquil suburban lifestyle with a strong sense of community and convenient access to shopping and dining options. It's an ideal location for those seeking a quiet, old Florida feel and just a quick drive away from downtown Jacksonville.`,
        map: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d130882.8858881099!2d-81.65863429872454!3d30.16685025697432!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e5b1ab17fd51b1%3A0xf6a3e36a0d88e83e!2sMandarin%2C%20Jacksonville%2C%20FL!5e1!3m2!1sen!2sus!4v1724381124567!5m2!1sen!2sus",
    },
    {
        id: "nwstjohns",
        name: "Homes in Julington Creek",
        description:
            `Northwest St. Johns County is a rapidly growing area renowned for its new developments and easy access to the beaches, downtown Jacksonville, and St. Augustine. This area features a mix of suburban and rural living, with plenty of parks, nature preserves, and easy access to the St. Johns River. It's a great place for those looking for modern amenities, spacious homes, and a strong community feel.`,
        map: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d131007.5732298162!2d-81.56458029951067!3d30.102835164755048!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e5b1ab17fd51b1%3A0x94e7a61a1bc0d819!2sSt.%20Johns%20County%2C%20FL!5e1!3m2!1sen!2sus!4v1724381264567!5m2!1sen!2sus",
    },
    {
        id: "pontevedra",
        name: "Ponte Vedra",
        description: `Ponte Vedra is known for its luxurious homes, world-class golf courses, and pristine beaches. It's a popular destination for those seeking an upscale, peaceful living environment with top-rated amenities.`,
        map: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d261818.63692848466!2d-81.47761969934157!3d30.18674611371515!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e436563c9d8eaf%3A0xa5d7f93a70e79b55!2sPalm%20Valley%2C%20FL%2032082!5e1!3m2!1sen!2sus!4v1724380998768!5m2!1sen!2sus",
    },
    {
        id: "rivertown",
        name: "County Road 210 Corridor",
        description: `
One of the most sought-after neighborhoods in this region is Rivertown. RiverTown in St. Johns County, Florida, offers a unique blend of suburban tranquility and riverfront living. This master-planned community features a variety of housing options, including single-family homes and town-homes. Residents enjoy resort-style amenities such as multiple amenity centers, swimming pools, fitness centers, and miles of hiking and biking trails, with many picturesque views of the St. Johns River. The community's design emphasizes a connection to nature, with ample green spaces and river access for kayaking and fishing. Conveniently located near both Jacksonville and St. Augustine, RiverTown provides a harmonious balance of peaceful living and accessibility to urban conveniences.`,
        map: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13817.2305382078!2d-81.6478966633437!3d30.028030942660237!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e5d1a77a97121d%3A0x64ba5033c5b0db35!2sRivertown%20Development%2C%20FL%2032259!5e0!3m2!1sen!2sus!4v1730657784030!5m2!1sen!2sus",
    },
];
</script>
